class PredictiveSearch extends HTMLElement {
  static MAX_SUPPORT_RESULTS = 7;

  constructor() {
    super();

    this.cachedResults = {};
    this.supportItems = [];
    this.supportItemsLoaded = false;
    this.supportItemsLoading = false;

    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');

    this.supportHeading = this.getAttribute('data-support-heading');
    this.supportTypes = (this.getAttribute('data-support-types') || '')
      .split(',')
      .map((type) => type.trim())
      .filter(Boolean);

    this.supportCacheKey = this.supportTypes.join('|');
    this.externalResultsContainer = null;

    const externalResultsSelector = this.getAttribute('data-search-results-target');
    if (externalResultsSelector) {
      this.externalResultsContainer = document.querySelector(externalResultsSelector);
    }

    this.predictiveSearchResults =
      this.externalResultsContainer?.querySelector('[data-predictive-search]') ||
      this.querySelector('[data-predictive-search]');

    this.supportResultsContainer =
      this.externalResultsContainer?.querySelector('[data-support-results]') ||
      this.querySelector('[data-support-results]');

    this.searchRecommendationsContainers = [];

    if (this.externalResultsContainer) {
      this.searchRecommendationsContainers = Array.from(
        this.externalResultsContainer.querySelectorAll('.search-recommendations')
      );
    }

    if (!this.searchRecommendationsContainers.length) {
      const localSearchScope =
        this.closest('.site-header-group') ||
        this.closest('[data-drawer]') ||
        this;

      this.searchRecommendationsContainers = Array.from(
        localSearchScope.querySelectorAll('.search-recommendations')
      );
    }

    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');

    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    if (this.input) {
      this.input.form?.addEventListener('reset', this.#onFormReset.bind(this));
      this.input.addEventListener(
        'input',
        debounce((event) => {
          this.#onChange(event);
        }, 300).bind(this)
      );

      if (this.input.value !== '') {
        this.#onFocus();
      }
    }

    this.#setupEventListeners();
    this.#toggleResetButton();
  }

  #setupEventListeners() {
    if (!this.input?.form) return;

    this.input.form.addEventListener('submit', this.#onFormSubmit.bind(this));
    this.input.addEventListener('focus', this.#onFocus.bind(this));
    // this.addEventListener('focusout', this.#onFocusOut.bind(this));
    this.addEventListener('keyup', this.#onKeyup.bind(this));
    this.addEventListener('keydown', this.#onKeydown.bind(this));
  }

  #getResultsScope() {
    return this.externalResultsContainer || this;
  }

  #getSupportResultsContainer() {
    const resultsScope = this.#getResultsScope();
    const supportContainers = Array.from(
      resultsScope.querySelectorAll('[data-support-results]')
    );
    const predictiveSupportContainer = this.predictiveSearchResults?.querySelector(
      '[data-support-results]'
    );

    this.supportResultsContainer = predictiveSupportContainer || supportContainers[0] || null;

    supportContainers.forEach((container) => {
      if (container !== this.supportResultsContainer) {
        container.innerHTML = '';
        container.hidden = true;
      }
    });

    return this.supportResultsContainer;
  }

  #getQuery() {
    return this.input.value.trim();
  }

  #onChange() {
    this.#toggleResetButton();

    const newSearchTerm = this.#getQuery();

    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.#getResultsScope()
        .querySelector('#predictive-search-results-groups-wrapper')
        ?.remove();
    }

    this.#updateSearchForTerm(this.searchTerm, newSearchTerm);
    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.#getSearchResults(this.searchTerm);
  }

  #onFormSubmit(event) {
    if (!this.#getQuery().length) {
      event.preventDefault();
      return;
    }

    this.input.value = this.input.value.trim().toLowerCase();
  }

  #onFormReset(event) {
    event.preventDefault();

    this.input.value = '';
    this.#toggleResetButton();
    this.searchTerm = '';

    this.abortController.abort();
    this.abortController = new AbortController();

    this.#closeResults(true);
  }

  #onFocus() {
    this.#loadSupportItems();

    const currentSearchTerm = this.#getQuery();
    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      this.#onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.#filterAndRenderSupportResults(currentSearchTerm);
      this.#open();
    } else {
      this.#getSearchResults(this.searchTerm);
    }
  }

  #onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  #onKeyup(event) {
    if (!this.#getQuery().length) this.close(true);

    switch (event.code) {
      case 'ArrowUp':
      case 'ArrowDown':
        if (!this.getAttribute('open')) return;
        event.preventDefault();

        if (event.code === 'ArrowUp') {
          this.#switchOption('up');
        } else {
          this.#switchOption('down');
        }
        break;

      case 'Enter':
        if (this.getAttribute('open')) {
          event.preventDefault();
          this.#selectOption();
        }
        break;

      default:
        break;
    }
  }

  #onKeydown(event) {
    if (event.code !== 'ArrowUp' && event.code !== 'ArrowDown') return;
    if (!this.hasAttribute('open')) return;
    if (event.target !== this.input) return;

    event.preventDefault();
  }

  #updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector(
      '[data-predictive-search-search-for-text]'
    );
    const currentButtonText = searchForTextElement?.textContent;
    if (!currentButtonText || !previousTerm) return;

    const matches = currentButtonText.match(new RegExp(previousTerm, 'g'));
    if (matches && matches.length > 1) return;

    const newButtonText = currentButtonText.replace(previousTerm, newTerm);
    searchForTextElement.textContent = newButtonText;
  }

  #switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const resultsScope = this.#getResultsScope();
    const selectedElement = resultsScope.querySelector('[aria-selected="true"]');

    const allVisibleElements = Array.from(
      resultsScope.querySelectorAll('li, button.predictive-search__item')
    ).filter((element) => element.offsetParent !== null);

    if (!allVisibleElements.length) return;

    let activeElementIndex = 0;
    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;
    while (selectedElementIndex === -1 && i < allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i += 1;
    }

    if (this.statusElement) {
      this.statusElement.textContent = '';
    }

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1
          ? 0
          : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0
          ? allVisibleElements.length - 1
          : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];
    activeElement.setAttribute('aria-selected', 'true');

    if (selectedElement) {
      selectedElement.setAttribute('aria-selected', 'false');
    }

    this.input.setAttribute('aria-activedescendant', activeElement.id || '');
  }

  #selectOption() {
    const selectedOption = this.#getResultsScope().querySelector(
      '[aria-selected="true"] a, button[aria-selected="true"]'
    );
    if (selectedOption) selectedOption.click();
  }

  #getSearchResults(searchTerm) {
    const normalizedSearchTerm = searchTerm.toLowerCase();
    const queryKey = normalizedSearchTerm.replace(/\s+/g, '-');

    this.#toggleSearchRecommendations(true);
    this.#filterAndRenderSupportResults(searchTerm);

    if (this.cachedResults[queryKey]) {
      this.#renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    const searchParams = new URLSearchParams({
      q: normalizedSearchTerm,
      section_id: 'predictive-search',
      'resources[type]': 'product,article,page,collection,query',
      'resources[limit]': '4',
      'resources[limit_scope]': 'each',
    });

    fetch(
      `${routes.predictive_search_url}?${searchParams.toString()}`,
      { signal: this.abortController.signal }
    )
      .then((response) => {
        if (!response.ok) {
          const error = new Error(response.status);
          this.close();
          throw error;
        }
        return response.text();
      })
      .then((text) => {
        const section = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search');

        const resultsMarkup = section ? section.innerHTML : '';
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });

        this.#renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.name === 'AbortError' || error?.code === 20) {
          return;
        }
        this.close();
        throw error;
      });
  }

  async #loadSupportItems() {
    if (!PredictiveSearch.supportItemsCache) {
      PredictiveSearch.supportItemsCache = {};
    }
    if (!PredictiveSearch.supportItemsPromise) {
      PredictiveSearch.supportItemsPromise = {};
    }

    if (Array.isArray(PredictiveSearch.supportItemsCache[this.supportCacheKey])) {
      this.supportItems = PredictiveSearch.supportItemsCache[this.supportCacheKey];
      this.supportItemsLoaded = true;
      return;
    }

    if (!PredictiveSearch.supportItemsPromise[this.supportCacheKey]) {
      PredictiveSearch.supportItemsPromise[this.supportCacheKey] = this.#fetchAllSupportItems()
        .then((supportItems) => {
          PredictiveSearch.supportItemsCache[this.supportCacheKey] = supportItems;
          return supportItems;
        })
        .catch(() => {
          PredictiveSearch.supportItemsCache[this.supportCacheKey] = [];
          return [];
        });
    }

    this.supportItemsLoading = true;
    const supportItems = await PredictiveSearch.supportItemsPromise[this.supportCacheKey];
    this.supportItems = supportItems;
    this.supportItemsLoaded = true;
    this.supportItemsLoading = false;

    this.#filterAndRenderSupportResults(this.#getQuery());
  }

  async #fetchAllSupportItems() {
    const allItems = [];

    for (const supportType of this.supportTypes) {
      let nextPageUrl = new URL(routes.search_url, window.location.origin);
      nextPageUrl.searchParams.set('view', 'support');
      nextPageUrl.searchParams.set('type', supportType);

      while (nextPageUrl) {
        const response = await fetch(nextPageUrl.toString(), {
          headers: {
            Accept: 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(response.status);
        }

        const supportResponse = await response.json();
        const pageItems = Array.isArray(supportResponse.items)
          ? supportResponse.items
          : [];

        allItems.push(...pageItems);

        nextPageUrl = supportResponse.next_page_url
          ? new URL(supportResponse.next_page_url, window.location.origin)
          : null;
      }
    }

    return allItems;
  }

  #filterAndRenderSupportResults(searchTerm) {
    if (!this.#getSupportResultsContainer()) return;

    if (!this.supportItemsLoaded) {
      if (!this.supportItemsLoading) {
        this.#loadSupportItems();
      }
      return;
    }

    const normalizedTerm = (searchTerm || '').trim().toLowerCase();
    if (!normalizedTerm.length) {
      this.#clearSupportResults();
      return;
    }

    const filteredItems = this.supportItems.filter((item) => {
      const title = (item?.title || '').toLowerCase();
      return title.includes(normalizedTerm);
    });

    this.#renderSupportResults(filteredItems, normalizedTerm);
  }

  #renderSupportResults(items, normalizedTerm = '') {
    const supportResultsContainer = this.#getSupportResultsContainer();
    if (!supportResultsContainer) return;

    if (!items || items.length === 0) {
      this.#clearSupportResults();
      return;
    }

    const visibleItems = items.slice(0, PredictiveSearch.MAX_SUPPORT_RESULTS);
    const listItems = visibleItems
      .map((item) => {
        const highlightedTitle = this.#highlightQueryInText(
          item.title || '',
          normalizedTerm
        );
        const url = this.#escapeHtml(item.url || '#');

        return `
          <li class="predictive-search__list-item" role="option" aria-selected="false">
            <a href="${url}" class="predictive-search__item">
              <div class="predictive-search__item-content predictive-search__item-content--centered">
                <p class="predictive-search__item-heading predictive-search__item-query-result">${highlightedTitle}</p>
              </div>
            </a>
          </li>
        `;
      })
      .join('');

    supportResultsContainer.innerHTML = `
      <div class="predictive-search__text-block search-recommendations--support">
        <h2 id="predictive-search-support" class="predictive-search__heading h6">${this.#escapeHtml(
          this.supportHeading
        )}</h2>
        <ul class="predictive-search__results-list list-unstyled" role="group" aria-labelledby="predictive-search-support">
          ${listItems}
        </ul>
      </div>
    `;

    supportResultsContainer.hidden = false;
    this.#syncEmptyStateVisibility();
  }

  #clearSupportResults() {
    const supportResultsContainer = this.#getSupportResultsContainer();
    if (!supportResultsContainer) return;

    supportResultsContainer.innerHTML = '';
    supportResultsContainer.hidden = true;
    this.#syncEmptyStateVisibility();
  }

  #escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  #highlightQueryInText(text, query) {
    const sourceText = String(text || '');
    const searchQuery = String(query || '').trim();

    if (!searchQuery.length) return this.#escapeHtml(sourceText);

    const sourceLower = sourceText.toLowerCase();
    const queryLower = searchQuery.toLowerCase();
    const matchIndex = sourceLower.indexOf(queryLower);

    if (matchIndex < 0) return this.#escapeHtml(sourceText);

    const before = sourceText.slice(0, matchIndex);
    const match = sourceText.slice(matchIndex, matchIndex + searchQuery.length);
    const after = sourceText.slice(matchIndex + searchQuery.length);

    return `${this.#escapeHtml(before)}<mark>${this.#escapeHtml(
      match
    )}</mark>${this.#escapeHtml(after)}`;
  }

  #queueDiscountPreviews(resultsMarkup) {
    if (!resultsMarkup || !window.theme?.queueDiscountPreview) return;

    const parsedResults = new DOMParser().parseFromString(resultsMarkup, 'text/html');
    const variantIds = [...parsedResults.querySelectorAll('[data-preview-variant-id]')]
      .map((price) => price.dataset.previewVariantId)
      .filter(Boolean);

    if (variantIds.length) theme.queueDiscountPreview(variantIds);
  }

  #setLiveRegionText(statusText) {
    if (!this.statusElement) return;

    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText || '';

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  #renderSearchResults(resultsMarkup) {
    if (!this.predictiveSearchResults) return;

    this.#queueDiscountPreviews(resultsMarkup);
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.#getSupportResultsContainer();
    this.#filterAndRenderSupportResults(this.searchTerm);

    this.setAttribute('results', 'true');
    this.#open();
    this.#setLiveRegionResults();
    this.#syncEmptyStateVisibility();
  }

  #setLiveRegionResults() {
    const liveRegionCountValue = this.#getResultsScope().querySelector(
      '[data-predictive-search-live-region-count-value]'
    );

    if (!liveRegionCountValue) return;
    this.#setLiveRegionText(liveRegionCountValue.textContent);
  }

  #open() {
    this.setAttribute('open', 'true');
    this.externalResultsContainer?.classList.add('is-open');
    this.#updateSearchRecommendationsVisibility();

    this.input.setAttribute('aria-expanded', 'true');
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.#closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  #closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
      this.searchTerm = '';
    }

    const selected = this.#getResultsScope().querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute('aria-selected', 'false');

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('open');
    this.externalResultsContainer?.classList.remove('is-open');

    this.#clearSupportResults();
    this.#updateSearchRecommendationsVisibility();
    this.#toggleResetButton();
    this.input.setAttribute('aria-expanded', 'false');

    this.resultsMaxHeight = false;
    this.predictiveSearchResults?.removeAttribute('style');
  }

  #toggleResetButton() {
    if (!this.resetButton) return;

    const resetIsHidden = this.resetButton.classList.contains('hidden');

    if (this.input.value.length > 0 && resetIsHidden) {
      this.resetButton.classList.remove('hidden');
    } else if (this.input.value.length === 0 && !resetIsHidden) {
      this.resetButton.classList.add('hidden');
    }
  }

  #toggleSearchRecommendations(isHidden) {
    if (!this.searchRecommendationsContainers.length) return;

    this.searchRecommendationsContainers.forEach((container) => {
      container.hidden = Boolean(isHidden);
    });
  }

  #syncEmptyStateVisibility() {
    const resultsScope = this.#getResultsScope();
    const emptyStateElement = resultsScope.querySelector('[data-predictive-search-empty-state]');
    const supportResultsContainer = this.#getSupportResultsContainer();
    const hasSupportResults = Boolean(
      supportResultsContainer &&
      !supportResultsContainer.hidden &&
      supportResultsContainer.querySelector('.predictive-search__list-item')
    );

    if (emptyStateElement) {
      emptyStateElement.hidden = hasSupportResults;
    }

    this.#updateSearchRecommendationsVisibility({
      hasSupportResults,
      emptyStateElement
    });
  }

  #updateSearchRecommendationsVisibility({ hasSupportResults, emptyStateElement } = {}) {
    if (!this.searchRecommendationsContainers.length) return;

    const hasQuery = Boolean(this.#getQuery().length);
    if (!hasQuery) {
      this.#toggleSearchRecommendations(false);
      return;
    }

    const resultsScope = this.#getResultsScope();
    const resolvedEmptyStateElement =
      typeof emptyStateElement !== 'undefined'
        ? emptyStateElement
        : resultsScope.querySelector('[data-predictive-search-empty-state]');

    const resolvedHasSupportResults =
      typeof hasSupportResults !== 'undefined'
        ? hasSupportResults
        : Boolean(
            this.supportResultsContainer &&
            !this.supportResultsContainer.hidden &&
            this.supportResultsContainer.querySelector('.predictive-search__list-item')
          );

    const hasNoResults = Boolean(resolvedEmptyStateElement) && !resolvedHasSupportResults;

    this.#toggleSearchRecommendations(!hasNoResults);
  }
}

customElements.define('predictive-search', PredictiveSearch);
PredictiveSearch.supportItemsCache = {};
PredictiveSearch.supportItemsPromise = {};
